import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, BarChart3, TrendingUp, Trophy, Target, PieChart, Activity, Wallet, RotateCcw, ArrowRight } from 'lucide-react';

const PLACEHOLDER_BLOCKS = [
    { id: 'bloc-1', permKey: 'stats_prospects', title: 'Prospects', subtitle: 'Sources, statuts & analyses', icon: BarChart3, accent: '#1B365D', route: '/statistiques/prospects', clickable: true },
    { id: 'bloc-2', permKey: 'stats_devis', title: 'Devis', subtitle: "Taux d'acceptation & conversion", icon: TrendingUp, accent: '#10B981', route: '/statistiques/devis-acceptation', clickable: true },
    { id: 'bloc-3', permKey: 'stats_leaderboard', title: 'Leaderboard', subtitle: 'Performance par commercial', icon: Trophy, accent: '#C9A962', route: '/statistiques/leaderboard', clickable: true },
    { id: 'bloc-4', permKey: 'stats_ca', title: 'Chiffre d\'affaires', subtitle: 'CA mensuel & tendance', icon: Target, accent: '#8B5CF6', route: '/statistiques/chiffre-affaires', clickable: true },
    { id: 'bloc-5', permKey: 'stats_tresorerie', title: 'Trésorerie', subtitle: 'Impayés & recouvrement', icon: PieChart, accent: '#EF4444', route: '/statistiques/tresorerie', clickable: true },
    { id: 'bloc-6', permKey: 'stats_activite', title: 'Activité', subtitle: 'Volumes, formules & destinations', icon: Activity, accent: '#0EA5E9', route: '/statistiques/activite', clickable: true },
    { id: 'bloc-7', permKey: 'stats_benefice', title: 'Bénéfice', subtitle: 'CA HT − frais = marge nette', icon: Wallet, accent: '#059669', route: '/statistiques/benefice', clickable: true },
    { id: 'bloc-8', permKey: 'stats_regagnes', title: 'Prospects regagnés', subtitle: 'Relances converties en visite/devis', icon: RotateCcw, accent: '#F97316', route: '/statistiques/prospects-regagnes', clickable: true },
];

export default function Statistiques() {
    const navigate = useNavigate();
    const { user } = useAuth();

    const isAdmin = user?.role === 'admin';
    const statsBlocksPerm = user?.permissions?.stats_blocks || {};
    const visibleBlocks = PLACEHOLDER_BLOCKS.filter(
        (b) => isAdmin || statsBlocksPerm[b.permKey] !== false
    );

    return (
        <Layout>
            <div className="space-y-6 animate-fadeIn" data-testid="statistiques-page">
                {/* Header */}
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(-1)}
                            className="h-8 w-8 p-0"
                            data-testid="stats-back-btn"
                            aria-label="Retour"
                        >
                            <ArrowLeft className="h-4 w-4" />
                        </Button>
                        <div className="h-10 w-10 rounded-full bg-[#1B365D]/10 flex items-center justify-center">
                            <BarChart3 className="h-5 w-5 text-[#1B365D]" />
                        </div>
                        <div>
                            <h1
                                className="text-2xl sm:text-3xl font-bold text-[#1B365D]"
                                style={{ fontFamily: 'Outfit, sans-serif' }}
                                data-testid="statistiques-title"
                            >
                                Statistiques
                            </h1>
                            <p className="text-gray-500 mt-0.5 text-sm">Indicateurs et analyses de performance</p>
                        </div>
                    </div>
                </div>

                {/* Placeholder grid (Bloc 1 cliquable + autres) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {visibleBlocks.length === 0 && (
                        <div className="col-span-full bg-white rounded-xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
                            Aucun indicateur accessible. Contactez votre administrateur pour obtenir des accès.
                        </div>
                    )}
                    {visibleBlocks.map((block, i) => {
                        const Icon = block.icon;
                        const isClickable = !!block.clickable;
                        return (
                            <Card
                                key={block.id}
                                className={`border-t-4 shadow-sm transition-all duration-300 animate-fadeIn ${isClickable ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5' : 'hover:shadow-md'}`}
                                style={{ borderTopColor: block.accent, animationDelay: `${i * 50}ms` }}
                                data-testid={`stats-${block.id}`}
                                onClick={isClickable ? () => navigate(block.route) : undefined}
                            >
                                <CardContent className="p-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-gray-500 text-sm font-medium uppercase tracking-wide">
                                                {block.title}
                                            </p>
                                            <p className="text-3xl font-bold mt-1 text-[#1B365D]">{isClickable ? 'Voir' : '—'}</p>
                                        </div>
                                        <div
                                            className="h-12 w-12 rounded-full flex items-center justify-center"
                                            style={{ backgroundColor: `${block.accent}1A` }}
                                        >
                                            <Icon className="h-6 w-6" style={{ color: block.accent }} />
                                        </div>
                                    </div>
                                    <p className="text-gray-400 text-sm mt-3 flex items-center gap-1">
                                        {block.subtitle}
                                        {isClickable && <ArrowRight className="w-3.5 h-3.5" />}
                                    </p>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            </div>
        </Layout>
    );
}
